import React, { useState, useEffect, useCallback } from 'react';
import {
    Paper, Typography, Button, Box, Radio, RadioGroup, FormControlLabel,
    FormControl, InputLabel, Select, MenuItem, Checkbox, TextField, Chip, SelectChangeEvent, CircularProgress
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import styles from './ToggleViews.module.scss';
import { getAllChecklistGroups, generateChecklistThreeDays } from 'core/API/checklist';
import { useAssignmentWorkplaceUsers } from 'core/hooks/useAssignmentWorkplaceUsers';
import { apiService } from 'core/services/apiService';
import { useToastContext } from 'core/components/Toast/context/ToastContext';

interface ViewProps {
    onBack: () => void;
    headerData: any;
    existingScheduleData?: any; // Optional prop for prefilling form
}

interface ChecklistGroup {
    id: number;
    name: string;
    label?: string;
}

interface User {
    id: number;
    firstName: string;
    lastName: string;
    email?: string;
}

const EMPTY_USERS: User[] = [];

const ToggleViews: React.FC<ViewProps> = ({ onBack, headerData, existingScheduleData }) => {
    const { showToast } = useToastContext();
    
    const [selectedOption, setSelectedOption] = useState<'assignmentGroup' | 'person'>('assignmentGroup');
    const [selectedValue, setSelectedValue] = useState<string>('');
    const [timeUnit, setTimeUnit] = useState<'hours' | 'days'>('hours');
    const [selectedTime, setSelectedTime] = useState<string>('02:00');
    const [scheduleTime, setScheduleTime] = useState<string>('02:00'); // Separate state for schedule time
    const [scheduleOption, setScheduleOption] = useState<string>(headerData[0]?.frequency || 'Daily');
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [selectedMonthDay, setSelectedMonthDay] = useState<string>('1');

    // New state for multiple time selections
    const [selectedTimes, setSelectedTimes] = useState<string[]>(['09:00']);
    const [newTime, setNewTime] = useState<dayjs.Dayjs | null>(dayjs());
    // New state for multiple week days and monthly dates
    const [selectedWeekDays, setSelectedWeekDays] = useState<string[]>(['Monday']);
    const [selectedMonthDates, setSelectedMonthDates] = useState<string[]>(['1']);
    // New state for monthly recurring patterns
    const [selectedMonthPattern, setSelectedMonthPattern] = useState<string>('second Thursday');
    const [selectedMonthPatternWeek, setSelectedMonthPatternWeek] = useState<string>('second');
    const [selectedMonthPatternDay, setSelectedMonthPatternDay] = useState<string>('Thursday');

    // New state for dynamic data from APIs
    const [checklistGroups, setChecklistGroups] = useState<ChecklistGroup[]>([]);
    // Groups fetch + schedule save
    const [requestLoading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Track which datasets have already been fetched so we don't reload on option switch
    const [groupsLoaded, setGroupsLoaded] = useState(false);

    // Users are cached per workplace; only fetched once the Person option is chosen
    const {
        data: users = EMPTY_USERS,
        isLoading: usersLoading,
        isError: usersError,
    } = useAssignmentWorkplaceUsers({ enabled: selectedOption === 'person' });
    const loading = requestLoading || (selectedOption === 'person' && usersLoading);

    // Helper function to get user ID from selected value
    const getUserIdFromSelectedValue = useCallback((selectedValue: string): number | null => {
        const user = users.find(u => `${u.firstName} ${u.lastName}`.trim() === selectedValue);
        return user ? user.id : null;
    }, [users]);

    // Helper function to get group ID from selected value
    const getGroupIdFromSelectedValue = useCallback((selectedValue: string): number | null => {
        const group = checklistGroups.find(g => (g.name || g.label) === selectedValue);
        return group ? group.id : null;
    }, [checklistGroups]);

    // Helper function to get current date in YYYY-MM-DD format
    const getCurrentDate = useCallback((): string => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    }, []);

    // Helper function to get frequency ID from frequency object
    const getFrequencyId = useCallback((frequency: { id: number; label: string }): number => {
        // Always use the frequency object ID directly - no static mapping needed
        return frequency?.id || 1;
    }, []);

    // Helper function to get recurring number
    const getRecurringNumber = useCallback((week: string): number => {
        const recurringMap: { [key: string]: number } = {
            'first': 1,
            'second': 2,
            'third': 3,
            'fourth': 4,
            'last': 5
        };
        return recurringMap[week] || 1;
    }, []);

    // Function to create the payload for checklist schedule activation
    const createSchedulePayload = useCallback(() => {
        const workplaceId = (typeof window !== 'undefined' && window.localStorage ? localStorage.getItem("workplace-id") : null) || "1";
        
        // Debug frequency data
        console.log('🔍 Frequency Debug:', {
            headerData: headerData[0],
            frequencyObject: headerData[0]?.frequencyObject,
            frequency: headerData[0]?.frequency,
            frequencyId: getFrequencyId(headerData[0]?.frequencyObject)
        });
        
        const basePayload = {
            workplace: `api/workplaces/${workplaceId}`,
            checklistTemplate: headerData && headerData[0]?.templateId ? `api/checklist_templates/${headerData[0].templateId}` : "api/checklist_templates/2", // Use provided templateId if available
            templateVersion: parseInt(headerData[0]?.version) || 1,
            assignmentType: selectedOption === 'person' ? 'assignee' : 'group',
            assignee: selectedOption === 'person' ? `api/users/${getUserIdFromSelectedValue(selectedValue)}` : null,
            group: selectedOption === 'assignmentGroup' ? `api/checklist_groups/${getGroupIdFromSelectedValue(selectedValue)}` : null,
            checklistFrequency: `api/checklist_frequencies/${getFrequencyId(headerData[0]?.frequencyObject)}`,
            pastDueAfterDay: timeUnit === 'days' ? parseInt(selectedTime) : 0,
            pastDueAfterHour: timeUnit === 'hours' ? selectedTime : ""
        };

        // Initialize all frequency-related fields to null/empty
        const frequencyFields = {
            onceDate: null,
            onceTime: null,
            dailyTimes: null,
            weeklyTimes: null,
            weekdayTimes: null,
            annualDate: null,
            annualTime: null,
            monthlyScheduleType: null,
            monthlyScheduleSpecificDates: null,
            monthlyScheduleRecurringDays: null
        };

        // Add frequency-specific fields based on selection
        switch (scheduleOption) {
            case 'Daily':
                return {
                    ...basePayload,
                    ...frequencyFields,
                    dailyTimes: selectedTimes.map(time => `${time}:00`)
                };

            case 'Weekday':
                return {
                    ...basePayload,
                    ...frequencyFields,
                    weekdayTimes: selectedTimes
                };

            case 'Weekly': {
                const weeklyTimes: any = {};
                selectedWeekDays.forEach(day => {
                    weeklyTimes[day.toLowerCase()] = selectedTimes;
                });
                return {
                    ...basePayload,
                    ...frequencyFields,
                    weeklyTimes
                };
            }

            case 'Monthly': {
                if (selectedMonthDay.includes(' ')) {
                    // Recurring pattern
                    return {
                        ...basePayload,
                        ...frequencyFields,
                        monthlyScheduleType: 'recurring_days',
                        monthlyScheduleRecurringDays: [{
                            recurring: getRecurringNumber(selectedMonthPatternWeek),
                            day: selectedMonthPatternDay.toLowerCase(),
                            times: selectedTimes
                        }]
                    };
                } else {
                    // Specific dates - use current month and year
                    const currentDate = new Date();
                    const currentYear = currentDate.getFullYear();
                    const currentMonth = currentDate.getMonth() + 1;
                    
                    return {
                        ...basePayload,
                        ...frequencyFields,
                        monthlyScheduleType: 'specific_dates',
                        monthlyScheduleSpecificDates: selectedMonthDates.map(date => ({
                            date: `${currentYear}-${currentMonth.toString().padStart(2, '0')}-${date.padStart(2, '0')}`,
                            times: selectedTimes
                        }))
                    };
                }
            }

            case 'Annually':
                return {
                    ...basePayload,
                    ...frequencyFields,
                    annualDate: selectedDate || getCurrentDate(),
                    annualTime: `${scheduleTime}:00`
                };

            case 'Once':
                return {
                    ...basePayload,
                    ...frequencyFields,
                    onceDate: selectedDate || getCurrentDate(),
                    onceTime: `${scheduleTime}:00`
                };

            default:
                return {
                    ...basePayload,
                    ...frequencyFields
                };
        }
    }, [
        headerData, 
        selectedOption, 
        selectedValue, 
        scheduleOption, 
        timeUnit, 
        selectedTime, 
        selectedTimes, 
        selectedWeekDays, 
        selectedMonthDay, 
        selectedMonthDates, 
        selectedMonthPatternWeek, 
        selectedMonthPatternDay, 
        selectedDate, 
        getUserIdFromSelectedValue, 
        getGroupIdFromSelectedValue, 
        getFrequencyId, 
        getRecurringNumber, 
        getCurrentDate
    ]);

    // Function to call the API for activating/updating checklist schedule
    const activateChecklistSchedule = useCallback(async (payload: any) => {
        try {
            setLoading(true);
            setError(null);

            let response;
            if (existingScheduleData?.id) {
                // From name-click flow with version confirmation: update via PUT
                response = await apiService.put(`/checklist_schedules/${existingScheduleData.id}`, payload);
                console.log('Checklist schedule updated successfully:', response);
                showToast('success', 'Checklist schedule updated successfully!');
            } else {
                // From toggle flow: create via POST (unchanged)
                response = await apiService.post('/checklist_schedules', payload);
                console.log('Checklist schedule created successfully:', response);
                showToast('success', 'Checklist schedule activated successfully!');
            }

            // Generate the next 3 days of events before returning, so the main page's
            // refetch picks them up. A failure here must not mask the successful save.
            try {
                await generateChecklistThreeDays();
            } catch (generateError) {
                console.error('Error generating 3 days checklist:', generateError);
                showToast(
                    'error',
                    'Schedule saved, but upcoming checklists could not be generated.'
                );
            }

            // Redirect to main page (parent refetches categorized events)
            onBack();
        } catch (error: any) {
            console.error('Error saving checklist schedule:', error);

            const errorMessage = error?.response?.data?.message ||
                               error?.message ||
                               'Failed to save checklist schedule. Please try again.';

            setError(errorMessage);
            showToast('error', errorMessage);
        } finally {
            setLoading(false);
        }
    }, [onBack, showToast, existingScheduleData]);

    // Fetch assignment groups only when that option is selected and not yet loaded
    useEffect(() => {
        if (selectedOption !== 'assignmentGroup' || groupsLoaded) return;

        const fetchGroups = async () => {
            try {
                setLoading(true);
                setError(null);

                const groupsData = await getAllChecklistGroups();
                console.log('Raw groups data from API:', groupsData);

                const safeGroupsData = Array.isArray(groupsData) ? groupsData : [];
                setChecklistGroups(safeGroupsData);
                setGroupsLoaded(true);

                // Set default value only when not prefilling from existing data
                if (!existingScheduleData && safeGroupsData.length > 0) {
                    const defaultValue = safeGroupsData[0]?.name || safeGroupsData[0]?.label || '';
                    if (defaultValue) setSelectedValue(defaultValue);
                }
            } catch (error) {
                setError('Failed to fetch assignment groups. Please try again.');
                setChecklistGroups([]);
            } finally {
                setLoading(false);
            }
        };

        fetchGroups();
    }, [selectedOption, groupsLoaded, existingScheduleData]);

    useEffect(() => {
        if (selectedOption === 'person' && usersError) {
            setError('Failed to fetch users. Please try again.');
        }
    }, [selectedOption, usersError]);

    // Prefill form with existing schedule data when available
    useEffect(() => {
        if (existingScheduleData) {
            // Set assignment type
            if (existingScheduleData.assignmentType === 'assignee') {
                setSelectedOption('person');
            } else if (existingScheduleData.assignmentType === 'group') {
                setSelectedOption('assignmentGroup');
            }
            
            // Set schedule option (frequency) from existing data if available
            if (existingScheduleData.checklistFrequency?.label) {
                setScheduleOption(existingScheduleData.checklistFrequency.label);
            }
            
            // Set assignment value
            if (existingScheduleData.assignmentType === 'assignee' && existingScheduleData.assignee) {
                const assigneeName = `${existingScheduleData.assignee.firstName || ''} ${existingScheduleData.assignee.lastName || ''}`.trim();
                setSelectedValue(assigneeName);
            } else if (existingScheduleData.assignmentType === 'group' && existingScheduleData.group) {
                const groupName = existingScheduleData.group.label || existingScheduleData.group.name;
                setSelectedValue(groupName);
            }
            
            // Frequency-specific data handling based on the detected frequency
            const frequency = existingScheduleData.checklistFrequency?.label;
            
            switch (frequency) {
                case 'Daily':
                    if (existingScheduleData.dailyTimes && Array.isArray(existingScheduleData.dailyTimes)) {
                        setSelectedTimes(existingScheduleData.dailyTimes);
                    }
                    break;
                    
                case 'Weekday':
                    if (existingScheduleData.weekdayTimes && Array.isArray(existingScheduleData.weekdayTimes)) {
                        setSelectedTimes(existingScheduleData.weekdayTimes);
                    }
                    break;
                    
                case 'Weekly':
                    if (existingScheduleData.weeklyTimes) {
                        const days = Object.keys(existingScheduleData.weeklyTimes);
                        const times = days.length > 0 ? existingScheduleData.weeklyTimes[days[0]] : [];
                        
                        // Capitalize day names to match weekDays array format (Monday, Tuesday, etc.)
                        const capitalizedDays = days.map(day => {
                            return day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();
                        });
                        
                        setSelectedWeekDays(capitalizedDays);
                        setSelectedTimes(times);
                    }
                    break;
                    
                case 'Monthly':
                    if (existingScheduleData.monthlyScheduleType === 'specific_dates' && 
                        existingScheduleData.monthlyScheduleSpecificDates && 
                        Array.isArray(existingScheduleData.monthlyScheduleSpecificDates)) {
                        
                        const dates: string[] = existingScheduleData.monthlyScheduleSpecificDates.map((item: any) => {
                            const day = new Date(item.date).getDate().toString();
                            return day;
                        });
                        
                        const uniqueDates = dates.filter((date, index) => dates.indexOf(date) === index).sort((a, b) => parseInt(a) - parseInt(b));
                        
                        setSelectedMonthDates(uniqueDates);
                        setSelectedMonthDay('1');
                        
                        if (existingScheduleData.monthlyScheduleSpecificDates[0]?.times) {
                            setSelectedTimes(existingScheduleData.monthlyScheduleSpecificDates[0].times);
                        }
                        
                    } else if (existingScheduleData.monthlyScheduleType === 'recurring_days' && 
                               existingScheduleData.monthlyScheduleRecurringDays && 
                               Array.isArray(existingScheduleData.monthlyScheduleRecurringDays)) {
                        
                        const recurringData = existingScheduleData.monthlyScheduleRecurringDays[0];
                        if (recurringData) {
                            const weekMap: { [key: number]: string } = {
                                1: 'first',
                                2: 'second', 
                                3: 'third',
                                4: 'fourth',
                                5: 'last'
                            };
                            
                            const week = weekMap[recurringData.recurring] || 'second';
                            const day = recurringData.day.charAt(0).toUpperCase() + recurringData.day.slice(1);
                            
                            setSelectedMonthPattern(`${week} ${day}`);
                            setSelectedMonthPatternWeek(week);
                            setSelectedMonthPatternDay(day);
                            setSelectedMonthDay(`${week} ${day}`);
                            
                            if (recurringData.times) {
                                setSelectedTimes(recurringData.times);
                            }
                        }
                    }
                    break;
                    
                case 'Annually':
                    if (existingScheduleData.annualDate) {
                        // Convert ISO date to YYYY-MM-DD format for date input
                        const date = new Date(existingScheduleData.annualDate);
                        const formattedDate = date.toISOString().split('T')[0];
                        setSelectedDate(formattedDate);
                    }
                    if (existingScheduleData.annualTime) {
                        // Convert time to HH:MM format
                        const time = existingScheduleData.annualTime;
                        const formattedTime = time.includes(':') ? time.substring(0, 5) : time;
                        setScheduleTime(formattedTime);
                    }
                    break;
                    
                case 'Once':
                    if (existingScheduleData.onceDate) {
                        // Convert ISO date to YYYY-MM-DD format for date input
                        const date = new Date(existingScheduleData.onceDate);
                        const formattedDate = date.toISOString().split('T')[0];
                        setSelectedDate(formattedDate);
                    }
                    if (existingScheduleData.onceTime) {
                        // Convert time to HH:MM format
                        const time = existingScheduleData.onceTime;
                        const formattedTime = time.includes(':') ? time.substring(0, 5) : time;
                        setScheduleTime(formattedTime);
                    }
                    break;
                    
                default:
                    // Fallback: Try to set any available time data for unknown frequencies
                    if (existingScheduleData.dailyTimes && Array.isArray(existingScheduleData.dailyTimes)) {
                        setSelectedTimes(existingScheduleData.dailyTimes);
                    } else if (existingScheduleData.weekdayTimes && Array.isArray(existingScheduleData.weekdayTimes)) {
                        setSelectedTimes(existingScheduleData.weekdayTimes);
                    } else if (existingScheduleData.annualTime) {
                        setSelectedTime(existingScheduleData.annualTime);
                    } else if (existingScheduleData.onceTime) {
                        setSelectedTime(existingScheduleData.onceTime);
                    }
                    break;
            }
            
            // Set past due time AFTER frequency-specific logic to avoid conflicts
            if (existingScheduleData.pastDueAfterHour && existingScheduleData.pastDueAfterHour.trim() !== '') {
                setSelectedTime(existingScheduleData.pastDueAfterHour);
                setTimeUnit('hours');
            } else if (existingScheduleData.pastDueAfterDay && existingScheduleData.pastDueAfterDay > 0) {
                setSelectedTime(existingScheduleData.pastDueAfterDay.toString());
                setTimeUnit('days');
            }
        }
    }, [existingScheduleData]);

    // Update selected value when option changes (only if not prefilled from existing data)
    useEffect(() => {
        if (existingScheduleData) {
            return;
        }
        
        if (selectedOption === 'assignmentGroup' && checklistGroups?.length > 0) {
            const defaultValue = checklistGroups[0]?.name || checklistGroups[0]?.label || '';
            if (defaultValue) {
                setSelectedValue(defaultValue);
            }
        } else if (selectedOption === 'person' && users?.length > 0) {
            const defaultValue = `${users[0]?.firstName || ''} ${users[0]?.lastName || ''}`.trim();
            if (defaultValue) {
                setSelectedValue(defaultValue);
            }
        }
    }, [selectedOption, checklistGroups, users, existingScheduleData]);

    // Additional effect to ensure assignee matching works correctly after users/groups data loads
    useEffect(() => {
        if (!existingScheduleData) return;

        if (existingScheduleData.assignmentType === 'assignee' && existingScheduleData.assignee && users?.length > 0) {
            const assigneeId = existingScheduleData.assignee.id;
            const matchingUser = users.find(user => user.id === assigneeId);

            if (matchingUser) {
                const correctName = `${matchingUser.firstName || ''} ${matchingUser.lastName || ''}`.trim();
                if (selectedValue !== correctName) {
                    setSelectedValue(correctName);
                }
            }
        } else if (existingScheduleData.assignmentType === 'group' && existingScheduleData.group && checklistGroups?.length > 0) {
            const groupId = existingScheduleData.group.id;
            const matchingGroup = checklistGroups.find(group => group.id === groupId);

            if (matchingGroup) {
                const correctName = matchingGroup.label || matchingGroup.name;
                if (selectedValue !== correctName) {
                    setSelectedValue(correctName);
                }
            }
        }
    }, [existingScheduleData, users, checklistGroups]);

    const handleVersionClick = () => {
        // Validate assignment selection
        if (!selectedValue && (checklistGroups?.length > 0 || users?.length > 0)) {
            showToast('error', 'Please select an assignment group or person.');
            return;
        }
        
        // Validate that required fields are filled based on schedule option
        if ((scheduleOption === 'Daily' || scheduleOption === 'Weekday') && selectedTimes.length === 0) {
            showToast('error', 'Please select at least one time for scheduling.');
            return;
        }
        if (scheduleOption === 'Weekly' && (selectedWeekDays.length === 0 || selectedTimes.length === 0)) {
            showToast('error', 'Please select at least one day and one time for weekly scheduling.');
            return;
        }
        if (scheduleOption === 'Monthly') {
            if (selectedMonthDay.includes(' ')) {
                // Recurring pattern option
                if (selectedTimes.length === 0) {
                    showToast('error', 'Please select at least one time for monthly scheduling.');
                    return;
                }
            } else {
                // Date option
                if (selectedMonthDates.length === 0 || selectedTimes.length === 0) {
                    showToast('error', 'Please select at least one date and one time for monthly scheduling.');
                    return;
                }
            }
        }
        if ((scheduleOption === 'Once' || scheduleOption === 'Annually') && (!selectedDate || !selectedTime)) {
            showToast('error', 'Please select both date and time for scheduling.');
            return;
        }

        // Create the payload
        const payload = createSchedulePayload();
        console.log('Schedule Payload:', payload);
        
        // Call the API to activate the schedule
        activateChecklistSchedule(payload);
    };

    const hours = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);
    const days = Array.from({ length: 10 }, (_, i) => (i + 1).toString());
    const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const monthWeeks = ['first', 'second', 'third', 'fourth', 'last'];

    const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as 'assignmentGroup' | 'person';
        
        setSelectedOption(value);
        
        // Set default value based on available data
        if (value === 'assignmentGroup' && checklistGroups?.length > 0) {
            const defaultValue = checklistGroups[0]?.name || checklistGroups[0]?.label || '';
            if (defaultValue) {
                setSelectedValue(defaultValue);
            }
        } else if (value === 'person' && users?.length > 0) {
            const defaultValue = `${users[0]?.firstName || ''} ${users[0]?.lastName || ''}`.trim();
            if (defaultValue) {
                setSelectedValue(defaultValue);
            }
        }
    };

    const handleScheduleOptionChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value as string;
        setScheduleOption(value);
        
        // Reset time selections when changing schedule options
        if (value === 'Daily' || value === 'Weekday') {
            setSelectedTimes(['09:00']); // Default time
        } else if (value === 'Weekly') {
            setSelectedWeekDays(['Monday']); // Default day
            setSelectedTimes(['09:00']); // Default time
        } else if (value === 'Monthly') {
            setSelectedMonthDates(['1']); // Default date
            setSelectedTimes(['09:00']); // Default time
            setSelectedMonthPattern('second Thursday'); // Default pattern
            setSelectedMonthPatternWeek('second');
            setSelectedMonthPatternDay('Thursday');
        } else if (value === 'Once' || value === 'Annually') {
            setSelectedDate('');
            setSelectedTime('09:00');
        }
    };

    const handleTimeUnitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as 'hours' | 'days';
        setTimeUnit(value);
        setSelectedTime(value === 'hours' ? '02:00' : '01:00');
    };

    const addTime = () => {
        if (newTime) {
            const timeString = newTime.format('HH:mm');
            if (!selectedTimes.includes(timeString)) {
                const updatedTimes = [...selectedTimes, timeString].sort();
                setSelectedTimes(updatedTimes);
                setNewTime(null);
            }
        }
    };

    const removeTime = (time: string) => {
        setSelectedTimes(selectedTimes.filter(t => t !== time));
    };

    const addWeekDay = (day: string) => {
        if (!selectedWeekDays.includes(day)) {
            setSelectedWeekDays([...selectedWeekDays, day].sort());
        }
    };

    const removeWeekDay = (day: string) => {
        setSelectedWeekDays(selectedWeekDays.filter(d => d !== day));
    };

    const addMonthDate = (date: string) => {
        if (!selectedMonthDates.includes(date)) {
            setSelectedMonthDates([...selectedMonthDates, date].sort((a, b) => parseInt(a) - parseInt(b)));
        }
    };

    const removeMonthDate = (date: string) => {
        setSelectedMonthDates(selectedMonthDates.filter(d => d !== date));
    };

    const updateMonthPattern = (week: string, day: string) => {
        const newPattern = `${week} ${day}`;
        setSelectedMonthPattern(newPattern);
        setSelectedMonthPatternWeek(week);
        setSelectedMonthPatternDay(day);
    };

    const formatTimeForDisplay = (time: string) => {
        if (!time) return '00:00';
        const [hours, minutes] = time.split(':');
        return `${hours.padStart(2, '0')}:${minutes || '00'}`;
    };

    // Helper function to convert 24-hour format (HH:mm) to 12-hour format (HH:MM AM/PM)
    const formatTimeTo12Hour = (time24: string): string => {
        if (!time24) return '12:00 AM';
        const [hours, minutes = '00'] = time24.split(':');
        const hour24 = parseInt(hours, 10);
        const minute = minutes.padStart(2, '0');
        
        if (hour24 === 0) {
            return `12:${minute} AM`;
        } else if (hour24 < 12) {
            return `${hour24}:${minute} AM`;
        } else if (hour24 === 12) {
            return `12:${minute} PM`;
        } else {
            return `${hour24 - 12}:${minute} PM`;
        }
    };

    const renderScheduleOptions = () => {
        switch (scheduleOption) {

            case 'Daily':
            case 'Weekday':
                return (
                    <Box mt={2}>
                        <Typography variant="subtitle1">Select Time(s):</Typography>
                        <Box display="flex" alignItems="center" mt={1} gap={1}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker
                                    value={newTime}
                                    onChange={(value) => setNewTime(value)}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            variant: "outlined",
                                            placeholder: "Select time"
                                        },
                                        desktopPaper: {
                                            sx: {
                                                '& .MuiMultiSectionDigitalClockSection-root': {
                                                    width: '75px',
                                                }
                                            }
                                        },
                                        mobilePaper: {
                                            sx: {
                                                '& .MuiMultiSectionDigitalClockSection-root': {
                                                    width: '75px',
                                                }
                                            }
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                            <Button
                                variant="contained"
                                onClick={addTime}
                                size="small"
                                disabled={!newTime}
                                sx={{ backgroundColor: '#446388', '&:hover': { backgroundColor: '#335577' } }}
                            >
                                Add Time
                            </Button>
                        </Box>
                        <Box mt={2}>
                            <Typography variant="body2" color="textSecondary" mb={1}>
                                Selected Times:
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                                {selectedTimes.map(time => (
                                    <Chip
                                        key={time}
                                        label={formatTimeTo12Hour(time)}
                                        onDelete={() => removeTime(time)}
                                        sx={{ backgroundColor: '#446388', color: 'white', '&:hover': { backgroundColor: '#335577' } }}
                                        variant="filled"
                                        size="small"
                                    />
                                ))}
                            </Box>
                            {selectedTimes.length === 0 && (
                                <Typography variant="body2" color="textSecondary" mt={1}>
                                    No times selected. Please add at least one time.
                                </Typography>
                            )}
                            {selectedTimes.length > 0 && (
                                <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
                                    <Typography variant="body2" color="textSecondary">
                                        {scheduleOption === 'Daily' 
                                            ? `Checklist will be scheduled daily at: ${selectedTimes.map(t => formatTimeTo12Hour(t)).join(', ')}`
                                            : `Checklist will be scheduled weekdays (Mon-Fri) at: ${selectedTimes.map(t => formatTimeTo12Hour(t)).join(', ')}`
                                        }
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                );
            case 'Weekly':
                return (
                    <Box mt={2}>
                        <Typography variant="subtitle1">Select Day(s) and Time(s):</Typography>
                        
                        {/* Day Selection */}
                        <Box mt={2}>
                            <Typography variant="body2" color="textSecondary" mb={1}>
                                Select Day(s) of Week:
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                {weekDays.map(day => (
                                    <Chip
                                        key={day}
                                        label={day}
                                        onClick={() => addWeekDay(day)}
                                        onDelete={selectedWeekDays.includes(day) ? () => removeWeekDay(day) : undefined}
                                        color={selectedWeekDays.includes(day) ? "primary" : "default"}
                                        variant={selectedWeekDays.includes(day) ? "filled" : "outlined"}
                                        size="small"
                                        style={{ cursor: 'pointer' }}
                                    />
                                ))}
                            </Box>
                        </Box>

                        {/* Time Selection */}
                        <Box mt={2}>
                            <Typography variant="body2" color="textSecondary" mb={1}>
                                Select Time(s):
                            </Typography>
                            <Box display="flex" alignItems="center" mt={1} gap={1}>
                                <TimePicker
                                    value={newTime}
                                    onChange={(value) => setNewTime(value)}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            variant: "outlined",
                                            placeholder: "Select time"
                                        },
                                        desktopPaper: {
                                            sx: {
                                                '& .MuiMultiSectionDigitalClockSection-root': {
                                                    width: '75px',
                                                }
                                            }
                                        },
                                        mobilePaper: {
                                            sx: {
                                                '& .MuiMultiSectionDigitalClockSection-root': {
                                                    width: '75px',
                                                }
                                            }
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={addTime}
                                    size="small"
                                    disabled={!newTime}
                                    sx={{ backgroundColor: '#446388', '&:hover': { backgroundColor: '#335577' } }}
                                >
                                    Add Time
                                </Button>
                            </Box>
                            <Box mt={2}>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {selectedTimes.map(time => (
                                        <Chip
                                            key={time}
                                            label={formatTimeTo12Hour(time)}
                                            onDelete={() => removeTime(time)}
                                            sx={{ backgroundColor: '#446388', color: 'white', '&:hover': { backgroundColor: '#335577' } }}
                                            variant="filled"
                                            size="small"
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Box>

                        {/* Summary */}
                        {selectedWeekDays.length > 0 && selectedTimes.length > 0 && (
                            <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
                                <Typography variant="body2" color="textSecondary">
                                    Checklist will be scheduled every {selectedWeekDays.join(', ')} at {selectedTimes.map(t => formatTimeTo12Hour(t)).join(', ')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                );
            case 'Monthly':
                return (
                    <Box mt={2}>
                        <Typography variant="subtitle1">Monthly Schedule Options:</Typography>
                        <RadioGroup
                            value={selectedMonthDay.includes(' ') ? 'weekday' : 'date'}
                            onChange={(e) => {
                                if (e.target.value === 'date') {
                                    setSelectedMonthDay('1');
                                    setSelectedMonthDates(['1']);
                                } else {
                                    setSelectedMonthDay('second Thursday');
                                    setSelectedMonthDates(['1']);
                                    setSelectedMonthPattern('second Thursday');
                                    setSelectedMonthPatternWeek('second');
                                    setSelectedMonthPatternDay('Thursday');
                                }
                            }}
                        >
                            <FormControlLabel
                                value="date"
                                control={<Radio />}
                                label="Specific date(s) (e.g., 1st, 15th of month)"
                            />
                            <FormControlLabel
                                value="weekday"
                                control={<Radio />}
                                label="Recurring day pattern (e.g., second Thursday)"
                            />
                        </RadioGroup>

                        {selectedMonthDay.includes(' ') ? (
                            // Recurring pattern option (updated for better UX)
                            <Box>
                                <Box display="flex" gap={2} mt={1}>
                                    <FormControl variant="outlined" size="small">
                                        <InputLabel>Week</InputLabel>
                                        <Select
                                            value={selectedMonthPatternWeek}
                                            onChange={(e: SelectChangeEvent<string>) => updateMonthPattern(e.target.value, selectedMonthPatternDay)}
                                            label="Week"
                                        >
                                            {monthWeeks.map(week => (
                                                <MenuItem key={week} value={week}>{week}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl variant="outlined" size="small">
                                        <InputLabel>Day</InputLabel>
                                        <Select
                                            value={selectedMonthPatternDay}
                                            onChange={(e: SelectChangeEvent<string>) => updateMonthPattern(selectedMonthPatternWeek, e.target.value)}
                                            label="Day"
                                        >
                                            {weekDays.map(day => (
                                                <MenuItem key={day} value={day}>{day}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                
                                {/* Time Selection for Recurring Pattern */}
                                <Box mt={2}>
                                    <Typography variant="body2" color="textSecondary" mb={1}>
                                        Select Time(s):
                                    </Typography>
                                    <Box display="flex" alignItems="center" mt={1} gap={1}>
                                        <TimePicker
                                            value={newTime}
                                            onChange={(value) => setNewTime(value)}
                                            slotProps={{
                                                textField: {
                                                    size: "small",
                                                    variant: "outlined",
                                                    placeholder: "Select time"
                                                }
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={addTime}
                                            size="small"
                                            disabled={!newTime}
                                            sx={{ backgroundColor: '#446388', '&:hover': { backgroundColor: '#335577' } }}
                                        >
                                            Add Time
                                        </Button>
                                    </Box>
                                    <Box mt={2}>
                                        <Box display="flex" flexWrap="wrap" gap={1}>
                                            {selectedTimes.map(time => (
                                                <Chip
                                                    key={time}
                                                    label={formatTimeTo12Hour(time)}
                                                    onDelete={() => removeTime(time)}
                                                    sx={{ backgroundColor: '#446388', color: 'white', '&:hover': { backgroundColor: '#335577' } }}
                                                    variant="filled"
                                                    size="small"
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            // Date option (updated for multiple dates)
                            <Box>
                                <Box mt={2}>
                                    <Typography variant="body2" color="textSecondary" mb={1}>
                                        Select Date(s) of Month:
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                        {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                                            <Chip
                                                key={day}
                                                label={day.toString()}
                                                onClick={() => addMonthDate(day.toString())}
                                                onDelete={selectedMonthDates.includes(day.toString()) ? () => removeMonthDate(day.toString()) : undefined}
                                                color={selectedMonthDates.includes(day.toString()) ? "primary" : "default"}
                                                variant={selectedMonthDates.includes(day.toString()) ? "filled" : "outlined"}
                                                size="small"
                                                style={{ cursor: 'pointer' }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                                
                                {/* Time Selection for Date */}
                                <Box mt={2}>
                                    <Typography variant="body2" color="textSecondary" mb={1}>
                                        Select Time(s):
                                    </Typography>
                                    <Box display="flex" alignItems="center" mt={1} gap={1}>
                                        <TimePicker
                                            value={newTime}
                                            onChange={(value) => setNewTime(value)}
                                            slotProps={{
                                                textField: {
                                                    size: "small",
                                                    variant: "outlined",
                                                    placeholder: "Select time"
                                                }
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={addTime}
                                            size="small"
                                            disabled={!newTime}
                                            sx={{ backgroundColor: '#446388', '&:hover': { backgroundColor: '#335577' } }}
                                        >
                                            Add Time
                                        </Button>
                                    </Box>
                                    <Box mt={2}>
                                        <Box display="flex" flexWrap="wrap" gap={1}>
                                            {selectedTimes.map(time => (
                                                <Chip
                                                    key={time}
                                                    label={formatTimeTo12Hour(time)}
                                                    onDelete={() => removeTime(time)}
                                                    sx={{ backgroundColor: '#446388', color: 'white', '&:hover': { backgroundColor: '#335577' } }}
                                                    variant="filled"
                                                    size="small"
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Summary */}
                        {selectedMonthDay.includes(' ') ? (
                            selectedTimes.length > 0 && (
                                <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
                                    <Typography variant="body2" color="textSecondary">
                                        Checklist will be scheduled on the {selectedMonthPattern} of every month at {selectedTimes.map(t => formatTimeTo12Hour(t)).join(', ')}
                                    </Typography>
                                </Box>
                            )
                        ) : (
                            selectedMonthDates.length > 0 && selectedTimes.length > 0 && (
                                <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
                                    <Typography variant="body2" color="textSecondary">
                                        Checklist will be scheduled on the {selectedMonthDates.join(', ')} of every month at {selectedTimes.map(t => formatTimeTo12Hour(t)).join(', ')}
                                    </Typography>
                                </Box>
                            )
                        )}
                    </Box>
                );
            case 'Annually':
                return (
                    <Box mt={2}>
                        <Typography variant="subtitle1">Select Date and Time:</Typography>
                        <TextField
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            style={{ marginTop: '10px' }}
                        />
                        <TextField
                            type="time"
                            value={formatTimeForDisplay(scheduleTime)}
                            onChange={(e) => setScheduleTime(e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                step: 300,
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            style={{ marginTop: '10px' }}
                        />
                        {selectedDate && scheduleTime && (
                            <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
                                <Typography variant="body2" color="textSecondary">
                                    Checklist will be scheduled every year on {selectedDate} at {formatTimeForDisplay(scheduleTime)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                );
            case 'Once':
                return (
                    <Box mt={2}>
                        <Typography variant="subtitle1">Select Date and Time:</Typography>
                        <TextField
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            style={{ marginTop: '10px' }}
                        />
                        <TextField
                            type="time"
                            value={formatTimeForDisplay(scheduleTime)}
                            onChange={(e) => setScheduleTime(e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                step: 300,
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            style={{ marginTop: '10px' }}
                        />
                        {selectedDate && scheduleTime && (
                            <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
                                <Typography variant="body2" color="textSecondary">
                                    Scheduled for: {selectedDate} at {formatTimeForDisplay(scheduleTime)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                );
            default:
                return null;
        }
    };

    // Frequency dropdown should always be disabled (read-only)
    const isFrequencyDisabled = true;

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Paper elevation={3} className={styles.toggleContainer}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} className={styles.header}>
                <Button variant="outlined" onClick={onBack} className={styles.backButton}>
                    Back
                </Button>
                <span style={{ cursor: loading ? 'not-allowed' : 'default' }}>
                    <Button className={styles.approveButton} onClick={handleVersionClick} disabled={loading} sx={{ backgroundColor: '#446388', '&:hover': { backgroundColor: '#335577' }, minWidth: 100, ...(loading && { pointerEvents: 'none' }) }}>
                        {loading ? (
                            <Box display="flex" alignItems="center" gap={1}>
                                <CircularProgress size={14} sx={{ color: 'white' }} />
                                Loading...
                            </Box>
                        ) : 'Activate'}
                    </Button>
                </span>
            </Box>


            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'rgb(245 245 245)' }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    {headerData.map((e: any, index: number) => (
                        <React.Fragment key={index}>
                            <Typography variant="body1"><strong>Version:</strong> {e.version}</Typography>
                            <Typography variant="body1"><strong>Checklist Name:</strong> {e.checklistName}</Typography>
                            <Typography variant="body1"><strong>Category:</strong> {e.category}</Typography>
                            <Typography variant="body1"><strong>Owner:</strong> {e.owner}</Typography>
                            <Typography variant="body1"><strong>Frequency:</strong> {e.frequency}</Typography>
                            <Typography variant="body1"><strong>Date:</strong> {e.date}</Typography>
                        </React.Fragment>
                    ))}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            checked={headerData[0]?.signoffRequired || false}
                            color="primary"
                            disabled
                        />
                        <Typography variant="body1">Sign Off</Typography>
                    </Box>
                </Box>
            </Box>


            <Box className={styles.selectionBox}>
                <Typography variant="h6" mb={2} className={styles.schedulerTitle}>
                    Checklist Assignment
                </Typography>
                
                <RadioGroup
                    row
                    value={selectedOption}
                    onChange={handleOptionChange}
                    className={styles.radioGroup}
                >
                    <FormControlLabel 
                        value="assignmentGroup" 
                        control={<Radio />} 
                        label="Assignment Group" 
                    />
                    <FormControlLabel 
                        value="person" 
                        control={<Radio />} 
                        label="Person" 
                    />
                </RadioGroup>
                
                <FormControl fullWidth variant="outlined" size="small" className={styles.formControl}>
                    <InputLabel id="select-option-label">
                        {selectedOption === 'assignmentGroup' ? 'Select Team' : 'Select Person'}
                    </InputLabel>
                    <Select
                        labelId="select-option-label"
                        value={selectedValue}
                        onChange={(e: SelectChangeEvent<string>) => setSelectedValue(e.target.value)}
                        label={selectedOption === 'assignmentGroup' ? 'Select Team' : 'Select Person'}
                        disabled={loading}
                        MenuProps={{
                            PaperProps: {
                                className: styles.dropdownMenu,
                            },
                        }}
                        className={styles.select}
                    >
                        {loading ? (
                            <MenuItem disabled>Loading assignment options...</MenuItem>
                        ) : !(selectedOption === 'assignmentGroup' ? checklistGroups : users) || (selectedOption === 'assignmentGroup' ? checklistGroups : users)?.length === 0 ? (
                            <MenuItem disabled>No options available</MenuItem>
                        ) : (
                            (selectedOption === 'assignmentGroup' ? checklistGroups : users)?.map((item) => (
                                <MenuItem key={item.id} value={
                                    selectedOption === 'assignmentGroup' 
                                        ? (item.name || item.label || '')
                                        : `${item.firstName || ''} ${item.lastName || ''}`.trim()
                                }>
                                    {selectedOption === 'assignmentGroup' 
                                        ? (item.name || item.label || '')
                                        : `${item.firstName || ''} ${item.lastName || ''}`.trim()
                                    }
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>
                
                {loading && (
                    <Box mt={1}>
                        <Typography variant="body2" color="textSecondary">
                            Loading assignment options...
                        </Typography>
                    </Box>
                )}
                
                {!loading && error && (
                    <Box mt={1}>
                        <Typography variant="body2" color="error.main">
                            {error}
                        </Typography>
                    </Box>
                )}


            </Box>

            <Box className={styles.schedulerBox}>
                <Typography variant="h6" mb={2} className={styles.schedulerTitle}>
                    Scheduling Options
                </Typography>
                <FormControl fullWidth variant="outlined" size="small" className={styles.formControl}>
                    <InputLabel id="schedule-option-label">Select Schedule</InputLabel>
                    <Select
                        labelId="schedule-option-label"
                        value={scheduleOption}
                        onChange={handleScheduleOptionChange}
                        label="Select Schedule"
                        disabled={isFrequencyDisabled}
                        MenuProps={{
                            PaperProps: {
                                className: styles.dropdownMenu,
                            },
                        }}
                        className={styles.select}
                    >
                        {['Daily', 'Weekday', 'Weekly', 'Monthly', 'Quarterly', 'Annually', 'Once'].map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {renderScheduleOptions()}
            </Box>

            <Box className={styles.schedulerBox}>
                <Typography variant="h6" mb={2} className={styles.schedulerTitle}>
                    Past Due Scheduler
                </Typography>
                <RadioGroup
                    row
                    value={timeUnit}
                    onChange={handleTimeUnitChange}
                    className={styles.radioGroup}
                >
                    <FormControlLabel 
                        value="hours" 
                        control={<Radio />} 
                        label="Hours" 
                    />
                    <FormControlLabel 
                        value="days" 
                        control={<Radio />} 
                        label="Days" 
                    />
                </RadioGroup>
                <FormControl fullWidth variant="outlined" size="small" className={styles.formControl}>
                    <InputLabel id="select-time-unit-label">{timeUnit === 'hours' ? 'Select Hours' : 'Select Days'}</InputLabel>
                    <Select
                        labelId="select-time-unit-label"
                        value={selectedTime}
                        onChange={(e: SelectChangeEvent<string>) => setSelectedTime(e.target.value)}
                        label={timeUnit === 'hours' ? 'Select Hours' : 'Select Days'}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    maxHeight: 100,
                                    overflowY: 'auto',
                                    mt: 1,
                                },
                            },
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                            },
                        }}
                        className={styles.select}
                    >
                        {(timeUnit === 'hours' ? hours : days).map((item: string) => (
                            <MenuItem key={item} value={item}>
                                {item}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </Paper>
        </LocalizationProvider>
    );
};

export default ToggleViews;